/**
 * Register Import Wizard — pure rules shared by ExcelUpload, FieldMapping and the in-place Preview.
 *
 * Everything here mirrors what the SERVER does today (RegisterDataService.Import.cs), so the
 * Preview never promises something the import will not do:
 *   - every parsed row is sent; the server decides (no client-side row dropping — AVA-602);
 *   - an empty mandatory value skips the row (SkipInvalidRows defaults to true);
 *   - Separated: a blank, unknown, ambiguous or stale Facility value skips the whole row (IMP-1, IMP-5);
 *   - duplicate identity values inside the file fail every row of the duplicate group;
 *   - an EMPTY identity value is NOT a skip — the row imports but cannot be updated later.
 * Spec: .claude/features/REGISTER_OWNERSHIP_MODES_SPEC.md (MODE-3..5, IMP-1..9, LEG-5).
 */

/** How the register owns its rows — decides the Facility column rules. */
export const REGISTER_KIND = Object.freeze({
  FACILITY: 'facility', // Facility register: rows always belong to the owner (OWN-2, IMP-4)
  GROUP_EMPTY: 'groupEmpty', // empty Facility Group register: the first import sets the mode (MODE-3)
  GROUP_SHARED: 'groupShared', // Shared: no Facility column (OWN-3, IMP-3)
  GROUP_SEPARATED: 'groupSeparated', // Separated: Facility column required (OWN-4, IMP-1)
  GROUP_UNSET: 'groupUnset', // has rows but no mode: import refused (LEG-5)
});

export const FACILITY_MODE = Object.freeze({ SHARED: 'Shared', SEPARATED: 'Separated' });

/** Why a Facility value does or does not resolve to a member Facility. */
export const FACILITY_MATCH = Object.freeze({
  MISSING: 'missing',
  EXACT: 'exact',
  ALIAS: 'alias',
  UNMAPPED: 'unmapped',
  AMBIGUOUS: 'ambiguous',
  STALE: 'stale',
});

/** Row issue codes the Preview shows (the server skips each of these rows). */
export const ROW_ISSUE = Object.freeze({
  MANDATORY: 'mandatory',
  FACILITY: 'facility',
  DUPLICATE_IDENTITY: 'duplicateIdentity',
});

const SKIPPING_MATCHES = new Set([
  FACILITY_MATCH.MISSING,
  FACILITY_MATCH.UNMAPPED,
  FACILITY_MATCH.AMBIGUOUS,
  FACILITY_MATCH.STALE,
]);

export function isGroupRegister(register) {
  return !!(register && register.facilityGroupId && !register.facilityId);
}

/**
 * @param {?object} register - RegisterVM (facilityId, facilityGroupId, facilityAssignmentMode)
 * @param {?number} rowCount - stored rows; null/undefined when unknown (treated as empty — the
 *   server still refuses a populated unset register, so this fails open, never closed)
 * @returns {string} one of REGISTER_KIND
 */
export function resolveRegisterKind(register, rowCount) {
  if (!isGroupRegister(register)) return REGISTER_KIND.FACILITY;
  const mode = register.facilityAssignmentMode;
  if (mode === FACILITY_MODE.SHARED) return REGISTER_KIND.GROUP_SHARED;
  if (mode === FACILITY_MODE.SEPARATED) return REGISTER_KIND.GROUP_SEPARATED;
  return Number(rowCount) > 0 ? REGISTER_KIND.GROUP_UNSET : REGISTER_KIND.GROUP_EMPTY;
}

/** The Facility column can be chosen only when it decides, or is part of, the mode. */
export function facilityColumnAllowed(kind) {
  return kind === REGISTER_KIND.GROUP_EMPTY || kind === REGISTER_KIND.GROUP_SEPARATED;
}

/** The mode after this import: the configured one, or the one an empty register's first import sets. */
export function resultingMode(kind, hasFacilityColumn) {
  if (kind === REGISTER_KIND.GROUP_SHARED) return FACILITY_MODE.SHARED;
  if (kind === REGISTER_KIND.GROUP_SEPARATED) return FACILITY_MODE.SEPARATED;
  if (kind === REGISTER_KIND.GROUP_EMPTY) return hasFacilityColumn ? FACILITY_MODE.SEPARATED : FACILITY_MODE.SHARED;
  return null;
}

/** Trim + case-insensitive, as the server matches Facility names and alias keys (IMP-5). */
export function normalizeFacilityKey(value) {
  return String(value ?? '').trim().toLowerCase();
}

export function isBlankCell(value) {
  return value === null || value === undefined || String(value).trim() === '';
}

/**
 * Distinct values of one column with their row counts, in first-seen order. Blank cells are
 * grouped into a single entry with `value: ''`; values that differ only by case/spaces are grouped.
 * @returns {Array<{value:string, key:string, rows:number}>}
 */
export function summarizeColumnValues(rows, column) {
  if (!column) return [];
  const byKey = new Map();
  (rows || []).forEach((row) => {
    const raw = row ? row[column] : undefined;
    const key = isBlankCell(raw) ? '' : normalizeFacilityKey(raw);
    const entry = byKey.get(key);
    if (entry) entry.rows += 1;
    else byKey.set(key, { value: key ? String(raw).trim() : '', key, rows: 1 });
  });
  return [...byKey.values()];
}

/**
 * Resolve one Facility value against aliases (which win) and the live members (IMP-5, IMP-6).
 * @param {*} value - the raw cell
 * @param {Array<{sourceValue:string, facilityId:string}>} aliases
 * @param {Array<{id:string, name:string}>} members - current member Facilities
 * @returns {{kind:string, facilityId:?string, facilityName:?string}}
 */
export function resolveFacilityValue(value, aliases, members) {
  if (isBlankCell(value)) return { kind: FACILITY_MATCH.MISSING, facilityId: null, facilityName: null };
  const key = normalizeFacilityKey(value);
  const memberList = members || [];
  const alias = (aliases || []).find((entry) => normalizeFacilityKey(entry.sourceValue) === key && entry.facilityId);
  if (alias) {
    const target = memberList.find((member) => member.id === alias.facilityId);
    return target
      ? { kind: FACILITY_MATCH.ALIAS, facilityId: target.id, facilityName: target.name }
      : { kind: FACILITY_MATCH.STALE, facilityId: alias.facilityId, facilityName: null };
  }
  const exact = memberList.filter((member) => normalizeFacilityKey(member.name) === key);
  if (exact.length === 1) return { kind: FACILITY_MATCH.EXACT, facilityId: exact[0].id, facilityName: exact[0].name };
  if (exact.length > 1) return { kind: FACILITY_MATCH.AMBIGUOUS, facilityId: null, facilityName: null };
  return { kind: FACILITY_MATCH.UNMAPPED, facilityId: null, facilityName: null };
}

export function facilityMatchSkips(kind) {
  return SKIPPING_MATCHES.has(kind);
}

/** The reason text the Preview shows for a skipped Facility value (row number stays in the table). */
export function facilitySkipReason(match, rawValue) {
  switch (match.kind) {
    case FACILITY_MATCH.MISSING: return 'Facility missing';
    case FACILITY_MATCH.AMBIGUOUS: return `Ambiguous Facility ‘${String(rawValue).trim()}’`;
    case FACILITY_MATCH.STALE: return `Mapped Facility is no longer in the Facility Group (‘${String(rawValue).trim()}’)`;
    default: return `Unknown Facility ‘${String(rawValue).trim()}’`;
  }
}

/**
 * Per-entry alias errors, aligned with `entries` ('' = valid). Mirrors facilityAliasErrors in
 * registerImportContracts.js (blank / duplicate / not a current member), worded for one row.
 */
export function aliasEntryErrors(entries, members) {
  const memberIds = new Set((members || []).map((member) => member.id));
  const firstIndexByKey = new Map();
  return (entries || []).map((entry, index) => {
    const key = normalizeFacilityKey(entry.sourceValue);
    if (!key) return 'Enter the value as it appears in the file.';
    if (firstIndexByKey.has(key)) {
      return `Duplicate — “${String(entry.sourceValue).trim()}” is already mapped. Remove one of them.`;
    }
    firstIndexByKey.set(key, index);
    if (!memberIds.has(entry.facilityId)) return 'Choose a Facility of the Facility Group.';
    return '';
  });
}

/** Identity values that appear on more than one row (the server fails every row in the group). */
export function duplicateIdentityValues(rows, identityColumn) {
  if (!identityColumn) return new Set();
  const seen = new Set();
  const duplicates = new Set();
  (rows || []).forEach((row) => {
    const raw = row ? row[identityColumn] : undefined;
    if (isBlankCell(raw)) return;
    const value = String(raw).trim();
    if (seen.has(value)) duplicates.add(value);
    else seen.add(value);
  });
  return duplicates;
}

/**
 * The first issue that makes the server skip this row, or null when it should import.
 * @param {object} row
 * @param {object} ctx - from buildPreviewContext
 * @returns {?{code:string, column:string, label:string}}
 */
export function predictRowIssue(row, ctx) {
  const data = row || {};
  const missing = (ctx.mandatory || []).find(({ column }) => isBlankCell(data[column]));
  if (missing) return { code: ROW_ISSUE.MANDATORY, column: missing.column, label: `missing ${missing.field}` };

  if (ctx.facility) {
    const raw = data[ctx.facility.column];
    const match = resolveFacilityValue(raw, ctx.facility.aliases, ctx.facility.members);
    if (facilityMatchSkips(match.kind)) {
      return { code: ROW_ISSUE.FACILITY, column: ctx.facility.column, label: facilitySkipReason(match, raw) };
    }
  }

  if (ctx.identityColumn && ctx.duplicateIdentities && ctx.duplicateIdentities.size) {
    const raw = data[ctx.identityColumn];
    if (!isBlankCell(raw) && ctx.duplicateIdentities.has(String(raw).trim())) {
      return { code: ROW_ISSUE.DUPLICATE_IDENTITY, column: ctx.identityColumn, label: `duplicate identity ‘${String(raw).trim()}’` };
    }
  }
  return null;
}

/**
 * Build the evaluation context once per mapping change.
 * @param {object} mapping - { mandatoryFields: {column: field}, identityColumn }
 * @param {?object} facility - { column, aliases, members } when Facility assignment applies AND the
 *   members are loaded; null otherwise (then no Facility skips are predicted)
 */
export function buildPreviewContext(rows, mapping, facility) {
  const mandatoryFields = (mapping && mapping.mandatoryFields) || {};
  const identityColumn = (mapping && mapping.identityColumn) || null;
  return {
    mandatory: Object.keys(mandatoryFields).map((column) => ({ column, field: mandatoryFields[column] || column })),
    identityColumn,
    duplicateIdentities: duplicateIdentityValues(rows, identityColumn),
    facility: facility && facility.column ? facility : null,
  };
}

/** Evaluate every row once: issues aligned with rows + the counts the Preview and footer show. */
export function evaluateRows(rows, ctx) {
  const list = rows || [];
  const counts = { total: list.length, ready: 0, mandatory: 0, facility: 0, duplicateIdentity: 0, noIdentity: 0 };
  const issues = list.map((row) => {
    const issue = predictRowIssue(row, ctx);
    if (!issue) {
      counts.ready += 1;
      if (ctx.identityColumn && isBlankCell((row || {})[ctx.identityColumn])) counts.noIdentity += 1;
    } else counts[issue.code] += 1;
    return issue;
  });
  counts.skipped = counts.total - counts.ready;
  return { issues, counts };
}

/**
 * Stat cards for the Complete step, read from `importResults.statistics`. The server counts a
 * skipped row in BOTH failedRows and skippedRows, so they are never added together.
 * `facilitySkippedRows` is optional (older APIs omit it) → null, and the card is not shown.
 */
export function importResultSummary(importResults) {
  const stats = (importResults && importResults.statistics) || {};
  const num = (value) => (Number.isFinite(Number(value)) ? Number(value) : 0);
  const hasFacilityField = Object.prototype.hasOwnProperty.call(stats, 'facilitySkippedRows');
  return {
    imported: num(stats.successfulRows),
    created: num(stats.newRows),
    updated: num(stats.updatedRows),
    notImported: Math.max(num(stats.failedRows), num(stats.skippedRows)),
    facilitySkipped: hasFacilityField ? num(stats.facilitySkippedRows) : null,
    groupLevel: num(stats.facilityGroupLevelRows),
  };
}

/**
 * The row-data key a server error most likely refers to, for highlighting. Matches a key quoted in
 * a message ('Name', "Name" or “Name”); Facility messages fall back to the Facility column.
 */
export function badFieldForError(error, facilityColumn) {
  const data = (error && error.rowData) || {};
  const keys = Object.keys(data);
  const messages = ((error && error.validationErrors) || []).map(String);
  const quoted = keys.find((key) => messages.some((message) =>
    message.includes(`'${key}'`) || message.includes(`"${key}"`) || message.includes(`“${key}”`) || message.startsWith(`${key}:`)));
  if (quoted) return quoted;
  if (facilityColumn && keys.includes(facilityColumn) && messages.some((message) => /facility/i.test(message))) {
    return facilityColumn;
  }
  return null;
}

/** Reason codes the server raises for a row it SAVED but could not link to a Tag. */
export const TAG_LINK_NOTICE_CODES = Object.freeze(['TAG_NOT_FOUND', 'LINK_FAILED']);

/**
 * The row number shown on the Complete step, in the same numbering as the Preview (file row, the
 * header being row 1). `sourceRowNumber` is the server's 1-based data-row position; older APIs
 * only send `rowIndex`, which is shown as-is.
 */
export function importErrorRowNumber(error) {
  const source = Number(error && error.sourceRowNumber);
  if (Number.isInteger(source) && source > 0) return source + 1;
  return Number(error && error.rowIndex) || 0;
}

/**
 * Splits the server's per-row entries into rows that were NOT imported (skipped or failed) and
 * rows that WERE imported but whose Tag could not be linked. The second group is a notice, not an
 * error: those rows are in the register. Both lists are in file order.
 */
export function classifyImportErrors(errors) {
  const list = Array.isArray(errors) ? errors : [];
  const byRow = (a, b) => importErrorRowNumber(a) - importErrorRowNumber(b);
  const isLinkNotice = (error) => !!error
    && !error.wasSkipped
    && TAG_LINK_NOTICE_CODES.includes(error.reasonCode);
  return {
    notImported: list.filter((error) => error && !isLinkNotice(error)).sort(byRow),
    notLinked: list.filter(isLinkNotice).sort(byRow),
  };
}
